import { notify } from '@affine/component';
import { BrandScreen } from '@affine/component/page-detail-skeleton';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import { formatOfflineAccessRemaining } from '../offline-access-format';
import { OfflineAccessService } from '../services/offline-access';
import * as styles from './offline-access-notices.css';

/** Locked doc whose offline grant ran out: explains how to restore access. */
export const OfflineAccessExpiredNotice = () => {
  const t = useI18n();
  return (
    <BrandScreen>
      <div className={styles.content} data-testid="offline-access-expired">
        <div className={styles.title}>
          {t['com.streamlain.offline-access.expired.title']()}
        </div>
        <div className={styles.description}>
          {t['com.streamlain.offline-access.expired.description']()}
        </div>
      </div>
    </BrandScreen>
  );
};

/**
 * Persistent in-app warnings while the offline window is in its last third
 * and while offline permissions could not be stored completely. Mounting it
 * also starts the workspace offline-access lifecycle (renewal, expiry and
 * system notification scheduling).
 */
export const OfflineAccessExpiryNotice = () => {
  const t = useI18n();
  const offlineAccess = useService(OfflineAccessService);
  const warning = useLiveData(offlineAccess.warning$);
  const vaultFull = useLiveData(offlineAccess.vaultFull$);
  const vaultFullToastRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!vaultFull) {
      if (vaultFullToastRef.current !== null) {
        notify.dismiss(vaultFullToastRef.current);
        vaultFullToastRef.current = null;
      }
      return;
    }
    if (vaultFullToastRef.current !== null) return;
    vaultFullToastRef.current = notify.warning(
      { title: t['com.streamlain.offline-access.vault-full']() },
      {
        duration: Infinity,
        onDismiss: () => {
          vaultFullToastRef.current = null;
        },
      }
    );
  }, [t, vaultFull]);
  const toastRef = useRef<{ id: string | number; expiresAt: number } | null>(
    null
  );
  // A warning the user closed stays closed for its expiry window.
  const dismissedExpiresAtRef = useRef<number | null>(null);

  useEffect(() => {
    const current = toastRef.current;
    if (!warning) {
      toastRef.current = null;
      if (current) notify.dismiss(current.id);
      return;
    }
    if (dismissedExpiresAtRef.current === warning.expiresAt) return;
    const message = t['com.streamlain.offline-access.warning.message']({
      remaining: formatOfflineAccessRemaining(warning.remainingMs),
    });
    const notification = {
      title: t['com.streamlain.offline-access.warning.title'](),
      message,
    };
    // Same expiry window: update the countdown in place; a new window
    // (after renewal + second offline period) shows the warning again.
    const id =
      current && current.expiresAt === warning.expiresAt
        ? current.id
        : undefined;
    const expiresAt = warning.expiresAt;
    toastRef.current = {
      id: notify.warning(notification, {
        id,
        duration: Infinity,
        onDismiss: () => {
          if (toastRef.current?.expiresAt !== expiresAt) return;
          dismissedExpiresAtRef.current = expiresAt;
          toastRef.current = null;
        },
      }),
      expiresAt,
    };
  }, [t, warning]);

  useEffect(
    () => () => {
      const current = toastRef.current;
      toastRef.current = null;
      if (current) notify.dismiss(current.id);
      if (vaultFullToastRef.current !== null) {
        notify.dismiss(vaultFullToastRef.current);
        vaultFullToastRef.current = null;
      }
    },
    []
  );

  return null;
};
